import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography, Slider, Stack, Tooltip, CircularProgress, TextField, Button, Paper, Alert } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LockIcon from '@mui/icons-material/Lock';
import * as pdfjsLib from 'pdfjs-dist/webpack';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;

/**
 * Visualizador de PDF completo com navegação de páginas e zoom
 * Suporta PDFs protegidos por senha
 */
const PdfViewer = ({ 
  pdfUrl, 
  fileName, 
  initialScale = 1.5, 
  showControls = true 
}) => {
  const canvasRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(initialScale);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [submittedPassword, setSubmittedPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  // Carregar PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        setPasswordError(false);
        
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          password: submittedPassword || undefined,
        });
        
        const pdfDocument = await loadingTask.promise;
        setPdf(pdfDocument);
        setTotalPages(pdfDocument.numPages);
        setLoading(false);
        setRequiresPassword(false);
      } catch (err) {
        console.error('Erro ao carregar PDF:', err);
        
        // Detectar se o PDF requer senha
        if (err.name === 'PasswordException') {
          setRequiresPassword(true);
          setLoading(false);
          
          if (submittedPassword) {
            // Senha incorreta
            setPasswordError(true);
            setError('Senha incorreta. Tente novamente.');
          } else {
            // PDF requer senha
            setError('Este PDF está protegido por senha.');
          }
        } else {
          setError('Erro ao carregar o PDF. Tente novamente.');
          setLoading(false);
        }
      }
    };

    if (pdfUrl) {
      loadPdf();
    }
  }, [pdfUrl, submittedPassword]);

  // Renderizar página atual
  useEffect(() => {
    const renderPage = async () => {
      if (!pdf || !canvasRef.current) return;

      try {
        setRendering(true);
        const page = await pdf.getPage(currentPage);
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        setRendering(false);
      } catch (err) {
        console.error('Erro ao renderizar página:', err);
        setRendering(false);
      }
    };

    renderPage();
  }, [pdf, currentPage, scale]);

  // Navegação com teclado (setas)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentPage < totalPages) {
          setCurrentPage(currentPage + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages]);

  // Controles de navegação
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = () => {
    const anchor = document.createElement('a');
    anchor.href = pdfUrl;
    anchor.download = fileName || 'documento.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleFullscreen = () => {
    if (canvasRef.current) {
      if (canvasRef.current.requestFullscreen) {
        canvasRef.current.requestFullscreen();
      }
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password.trim()) {
      setPasswordError(false);
      setSubmittedPassword(password.trim());
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Carregando PDF...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          p: 3,
          gap: 3,
        }}
      >
        {requiresPassword ? (
          <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <LockIcon color="primary" fontSize="large" />
              <Typography variant="h6" color="primary">
                PDF Protegido
              </Typography>
            </Box>
            
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Senha incorreta. Tente novamente.
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Este documento está protegido por senha. Digite a senha para visualizar.
            </Typography>
            
            <form onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                type="password"
                label="Senha do PDF"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={passwordError}
                autoFocus
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!password.trim()}
              >
                Desbloquear PDF
              </Button>
            </form>
          </Paper>
        ) : (
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {/* Controles */}
      {showControls && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: { xs: 0.5, sm: 1 },
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Navegação de páginas */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
            <Tooltip title="Página anterior (←)">
              <span>
                <IconButton 
                  size="small" 
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1 || rendering}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            
            <Typography variant="caption" sx={{ minWidth: { xs: 60, sm: 80 }, textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
              {currentPage} / {totalPages}
            </Typography>
            
            <Tooltip title="Próxima página (→)">
              <span>
                <IconButton 
                  size="small" 
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || rendering}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Controles de zoom */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" sx={{ minWidth: { xs: 150, sm: 200 } }}>
            <Tooltip title="Diminuir zoom">
              <span>
                <IconButton size="small" onClick={zoomOut} disabled={rendering}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            
            <Slider
              value={scale}
              onChange={(e, newValue) => setScale(newValue)}
              min={0.5}
              max={3}
              step={0.25}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              sx={{ width: { xs: 60, sm: 100 } }}
              disabled={rendering}
            />
            
            <Tooltip title="Aumentar zoom">
              <span>
                <IconButton size="small" onClick={zoomIn} disabled={rendering}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Ações */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
            <Tooltip title="Tela cheia">
              <IconButton size="small" onClick={handleFullscreen}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {fileName && (
              <Tooltip title="Baixar PDF">
                <IconButton size="small" onClick={handleDownload} color="primary">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}

      {/* Canvas do PDF */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2,
          bgcolor: '#525659',
          position: 'relative',
        }}
      >
        {rendering && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            height: 'auto',
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            opacity: rendering ? 0.5 : 1,
            transition: 'opacity 0.2s',
          }}
        />
      </Box>
    </Box>
  );
};

PdfViewer.propTypes = {
  pdfUrl: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  initialScale: PropTypes.number,
  showControls: PropTypes.bool,
};

export default PdfViewer;
